import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { clientAuthService, Client } from '../services/clientAuthService'
import { toast } from 'sonner'

interface ClientAuthContextType {
  client: Client | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateClient: (client: Client) => void
}

const ClientAuthContext = createContext<ClientAuthContextType | undefined>(undefined)

export const useClientAuth = () => {
  const context = useContext(ClientAuthContext)
  if (!context) {
    throw new Error('useClientAuth must be used within a ClientAuthProvider')
  }
  return context
}

export const ClientAuthProvider = ({ children }: { children: ReactNode }) => {
  const [client, setClient] = useState<Client | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('client_token'))
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    const loadClient = async () => {
      if (token) {
        try {
          const clientData = await clientAuthService.getProfile(token)
          // Transform ClientProfile to Client (minimal)
          setClient({
            id: clientData.id,
            uuid: '',
            email: clientData.email,
            first_name: clientData.first_name,
            last_name: clientData.last_name,
            phone: clientData.phone,
            is_active: clientData.is_active,
            created_at: new Date().toISOString(),
          })
        } catch (error) {
          console.error('Failed to load client:', error)
          localStorage.removeItem('client_token')
          setToken(null)
        }
      }
      setIsLoading(false)
    }
    loadClient()
  }, [token])

  const login = async (email: string, password: string) => {
    try {
      const { client, access_token } = await clientAuthService.login(email, password)
      setClient(client)
      setToken(access_token)
      localStorage.setItem('client_token', access_token)
      navigate('/client/dashboard')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Login failed'
      toast.error(message)
      throw error
    }
  }

  const logout = () => {
    setClient(null)
    setToken(null)
    localStorage.removeItem('client_token')
    navigate('/client/login')
  }

  const updateClient = (updatedClient: Client) => {
    setClient(updatedClient)
  }

  const value: ClientAuthContextType = {
    client,
    token,
    isAuthenticated: !!client,
    isLoading,
    login,
    logout,
    updateClient,
  }

  return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>
}